import {
  createUnifiedTheme,
  createBaseThemeOptions,
  palettes,
} from '@backstage/theme';

const customLightPallette = {
  ...palettes.light,
  primary: {
    main: '#B82025', // Salsa brand red
    light: '#af272f',
    dark: '#8a1a1e',
  },
  secondary: {
    main: '#292E33', // Dark grey
    light: '#414952',
    dark: '#14171A',
  },
  background: {
    default: '#F7F9FA', // Light grey background
    paper: '#ffffff', // White paper
  },
  text: {
    primary: '#292E33', // Dark grey text
    secondary: '#414952', // Medium grey text
  },
  error: {
    main: '#B82025',
    light: '#af272f',
    dark: '#8a1a1e',
  },
  navigation: {
    background: '#000000', // Black sidebar background
    indicator: '#B82025', // Red indicator for active item
    color: '#ffffff', // White text
    selectedColor: '#B82025', // Red for selected/active item
  },
  grey: {
    50: '#F7F9FA',
    100: '#E5E7EB',
    200: '#D5DAE0',
    300: '#9A9DA7',
    400: '#6B7280',
    500: '#414952',
    600: '#292E33',
    700: '#14171A',
    800: '#0A0C0E',
    900: '#000000',
  },
};

const customLightTheme = createUnifiedTheme({
  ...createBaseThemeOptions({
    palette: customLightPallette,
  }),
  fontFamily: '"Inter", "Point", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
  defaultPageTheme: 'home',
  pageTheme: {
    home: {
      colors: ['#F7F9FA', '#ffffff'],
      shape: 'linear-gradient(180deg, #F7F9FA 0%, #ffffff 100%)',
      backgroundImage: '',
      fontColor: '#292E33',
    },
    documentation: {
      colors: ['#F7F9FA', '#ffffff'],
      shape: 'linear-gradient(180deg, #F7F9FA 0%, #ffffff 100%)',
      backgroundImage: '',
      fontColor: '#292E33',
    },
    tool: {
      colors: ['#F7F9FA', '#ffffff'],
      shape: 'linear-gradient(180deg, #F7F9FA 0%, #ffffff 100%)',
      backgroundImage: '',
      fontColor: '#292E33',
    },
    service: {
      colors: ['#F7F9FA', '#ffffff'],
      shape: 'linear-gradient(180deg, #F7F9FA 0%, #ffffff 100%)',
      backgroundImage: '',
      fontColor: '#292E33',
    },
    website: {
      colors: ['#F7F9FA', '#ffffff'],
      shape: 'linear-gradient(180deg, #F7F9FA 0%, #ffffff 100%)',
      backgroundImage: '',
      fontColor: '#292E33',
    },
    library: {
      colors: ['#F7F9FA', '#ffffff'],
      shape: 'linear-gradient(180deg, #F7F9FA 0%, #ffffff 100%)',
      backgroundImage: '',
      fontColor: '#292E33',
    },
    other: {
      colors: ['#F7F9FA', '#ffffff'],
      shape: 'linear-gradient(180deg, #F7F9FA 0%, #ffffff 100%)',
      backgroundImage: '',
      fontColor: '#292E33',
    },
    app: {
      colors: ['#F7F9FA', '#ffffff'],
      shape: 'linear-gradient(180deg, #F7F9FA 0%, #ffffff 100%)',
      backgroundImage: '',
      fontColor: '#292E33',
    },
    apis: {
      colors: ['#F7F9FA', '#ffffff'],
      shape: 'linear-gradient(180deg, #F7F9FA 0%, #ffffff 100%)',
      backgroundImage: '',
      fontColor: '#292E33',
    },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: '#F7F9FA',
          color: '#292E33',
        },
      },
    },
  },
});


const customDarkPalette = {
  ...palettes.dark,
  primary: {
    main: '#E53935', // Brighter red for dark bg contrast
    light: '#FF6F60',
    dark: '#B82025',
  },
  secondary: {
    main: '#D5DAE0',
    light: '#E5E7EB',
    dark: '#9A9DA7',
  },
  background: {
    default: '#121317', // Deep charcoal
    paper: '#1E2025', // Slightly lighter cards/surfaces
  },
  text: {
    primary: '#E8EAED', // High contrast white-ish
    secondary: '#9A9DA7', // Muted grey for secondary text
  },
  error: {
    main: '#EF5350',
    light: '#FF6F60',
    dark: '#B82025',
  },
  navigation: {
    background: '#0A0B0D', // Near-black sidebar
    indicator: '#E53935',
    color: '#9A9DA7', // Muted nav text
    selectedColor: '#E53935',
    navItem: {
      hoverBackground: '#1E2025',
    },
  },
  grey: {
    50: '#1E2025',
    100: '#292E33',
    200: '#353A42',
    300: '#414952',
    400: '#6B7280',
    500: '#9A9DA7',
    600: '#D5DAE0',
    700: '#E5E7EB',
    800: '#F0F1F3',
    900: '#FFFFFF',
  },
};

const darkPageTheme = {
  colors: ['#121317', '#1E2025'] as [string, string],
  shape: 'linear-gradient(180deg, #121317 0%, #1E2025 100%)',
  backgroundImage: '',
  fontColor: '#E8EAED',
};

const customDarkTheme = createUnifiedTheme({
  ...createBaseThemeOptions({
    palette: customDarkPalette,
  }),
  fontFamily: '"Inter", "Point", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
  defaultPageTheme: 'home',
  pageTheme: {
    home: darkPageTheme,
    documentation: darkPageTheme,
    tool: darkPageTheme,
    service: darkPageTheme,
    website: darkPageTheme,
    library: darkPageTheme,
    other: darkPageTheme,
    app: darkPageTheme,
    apis: darkPageTheme,
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: '#121317',
          color: '#E8EAED',
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: '#0A0B0D',
          color: '#E8EAED',
          fontSize: '0.75rem',
          border: '1px solid #353A42',
        },
        arrow: {
          color: '#0A0B0D',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundColor: '#1E2025',
          color: '#E8EAED',
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          backgroundColor: '#1E2025',
          color: '#E8EAED',
        },
      },
    },
    MuiCardContent: {
      styleOverrides: {
        root: {
          backgroundColor: '#1E2025',
        },
      },
    },
    MuiCardHeader: {
      styleOverrides: {
        root: {
          backgroundColor: '#1E2025',
        },
        title: {
          color: '#E8EAED',
        },
        subheader: {
          color: '#9A9DA7',
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottomColor: '#353A42',
        },
        head: {
          backgroundColor: '#1E2025',
          color: '#9A9DA7',
        },
        body: {
          color: '#E8EAED',
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          backgroundColor: '#292E33',
          color: '#E8EAED',
        },
        outlined: {
          borderColor: '#353A42',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        outlined: {
          borderColor: '#353A42',
          color: '#E8EAED',
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        root: {
          backgroundColor: '#1E2025',
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          color: '#9A9DA7',
          '&.Mui-selected': {
            color: '#E8EAED',
          },
        },
      },
    },
    BackstageHeader: {
      styleOverrides: {
        header: {
          backgroundColor: '#121317',
          color: '#E8EAED',
          backgroundImage: 'none',
          boxShadow: 'none',
          borderBottom: '1px solid #353A42',
        },
        title: {
          color: '#E8EAED',
        },
        subtitle: {
          color: '#9A9DA7',
        },
      },
    },
    BackstageHeaderTabs: {
      styleOverrides: {
        tabsWrapper: {
          backgroundColor: '#121317',
        },
        defaultTab: {
          color: '#9A9DA7',
        },
      },
    },
    BackstageInfoCard: {
      styleOverrides: {
        header: {
          backgroundColor: '#1E2025',
          borderBottom: '1px solid #353A42',
        },
      },
    },
    BackstageItemCardHeader: {
      styleOverrides: {
        root: {
          backgroundColor: '#1E2025',
          color: '#E8EAED',
          backgroundImage: 'none',
        },
      },
    },
    BackstagePage: {
      styleOverrides: {
        root: {
          backgroundColor: '#121317',
        },
      },
    },
    BackstageContent: {
      styleOverrides: {
        root: {
          backgroundColor: '#121317',
        },
      },
    },
  },
});

export const salsaDarkTheme = customDarkTheme;
export const salsaLightTheme = customLightTheme;
